import type { NetworkClient } from '~/lib/bosca/networkclient'
import { Api } from '~/lib/bosca/api'
import {
    AddProgressDocument,
    GetProgressionDocument,
    GetProgressionsDocument,
    GetBookmarkDocument,
    GetBookmarksDocument,
    AddBookmarkDocument,
    RemoveBookmarkDocument,
    type MetadataFragment,
    type CollectionFragment,
    type ProgressionFragment, type ProfileInput, NewsletterSignupDocument,
} from '~/lib/graphql/graphql'
import type { AsyncData } from '#app/composables/asyncData'

export class Profiles<T extends NetworkClient> extends Api<T> {
    // eslint-disable-next-line
    constructor(network: T) {
        super(network)
    }

    async isBookmarked(id: string, version: number): Promise<boolean> {
        const response = await this.network.execute(GetBookmarkDocument, { id, version }, { authenticate: true })
        return (response?.profiles?.current?.bookmarks?.bookmark?.id || 0) > 0
    }

    getBookmarksAsyncData(): AsyncData<(MetadataFragment | CollectionFragment)[] | null, any> {
        return this.executeAndTransformAsyncData(
            GetBookmarksDocument, {},
            (response) => {
                if (!response?.profiles?.current?.bookmarks?.bookmarks) {
                    return []
                }
                return response.profiles.current.bookmarks.bookmarks.map((m) => {
                    return m.metadata || m.collection
                })
            },
            {
                authenticate: true,
            }
        )
    }

    async addBookmark(id: string, version: number) {
        await this.network.execute(AddBookmarkDocument, { id, version })
    }

    async removeBookmark(id: string, version: number) {
        await this.network.execute(RemoveBookmarkDocument, { id, version })
    }

    async addProgress(id: string, version: number, stepId: number): Promise<{
        nextStepId: number | null,
        started: any
    } | null> {
        const response = await this.network.execute(AddProgressDocument, { id, version, stepId: stepId })
        return response?.profiles?.profile?.addProgress as { nextStepId: number | null, started: any } | null
    }

    getProgressionAsyncData(
        id: string,
        version: number,
    ): AsyncData<ProgressionFragment | null, any> {
        return this.executeAndTransformAsyncData(
            GetProgressionDocument, { id, version },
            (response) => {
                return response?.profiles?.current?.guides?.progressions?.progress as ProgressionFragment | null
            },
            {
                authenticate: true,
            }
        )
    }

    getProgressionsAsyncData(): AsyncData<({
        metadata: MetadataFragment,
        nextStepId: number,
        percentage: number,
        started: any
    })[] | null, any> {
        return this.executeAndTransformAsyncData(
            GetProgressionsDocument, {},
            (response) => {
                return response?.profiles?.current?.guides?.progressions?.all as ({
                    metadata: MetadataFragment,
                    nextStepId: number,
                    percentage: number,
                    started: any
                })[] | null
            },
            {
                authenticate: true,
            }
        )
    }

    async addNewsletterSignup(profile: ProfileInput) {
        await this.network.execute(NewsletterSignupDocument, { profile })
    }
}
